import { Category } from '.prisma/client';
import { useAlertDialog } from '@/components/alert/dialog';
import LandingPage from '@/components/layout/landing-page';
import { useMediaEditor } from '@/components/media/editor';
import PlaylistControls from '@/components/playlist/controls';
import { usePlaylistEditor } from '@/components/playlist/editor';
import PlaylistItemControls from '@/components/playlist/item-controls';
import PlaylistItems from '@/components/playlist/items';
import TagCloud from '@/components/tag/cloud';
import UserCard from '@/components/user/card';
import {
	useDeletePlaylistItems, useMediaTypes, usePlaylist, useUpdatePlaylist, useUpdatePlaylistItems
} from '@/hooks/api';
import { fetchCategories } from '@/prisma/utils/categories';
import { MediaLevels } from '@/prisma/utils/media-levels';
import { fetchMediaTypes } from '@/prisma/utils/media-type';
import { fetchPlaylist, PlaylistItem } from '@/prisma/utils/playlist';
import { fetchTags, Tag } from '@/prisma/utils/tag';
import {
	Box, Divider, Heading, SimpleGrid, Text, useToast
} from '@chakra-ui/react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React, {
	useCallback, useEffect, useState
} from 'react';
import { QueryClient, useQueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';
import version from 'release.current-version';

type PlaylistDetailPageProps = {
	id: number;
	categories: Category[];
	tags: Tag[];
}

const PlaylistDetailPage: React.FC<PlaylistDetailPageProps> = (props: PlaylistDetailPageProps) => {
	const {
		id, categories, tags 
	} = props;
	const queryClient = useQueryClient();
	const toast = useToast();
	const [playlistItem, setPlaylistItem] = useState<PlaylistItem | undefined>(undefined);
	const { data: playlist } = usePlaylist(id);
	const { data: mediaTypes } = useMediaTypes();
	const {
		isOpen: isOpenEditor,
		openEditor,
		closeEditor,
		Editor: PlaylistEditor,
	} = usePlaylistEditor();

	const {
		isOpen: isOpenMediaEditor,
		openEditor: openMediaEditor,
		closeEditor: closeMediaEditor,
		Editor: MediaEditor,
	} = useMediaEditor();

	const {
		isOpen: isOpenAlertDialog, openDialog, closeDialog, AlertDialog,
	} = useAlertDialog();
	
	const updatePlaylist = useUpdatePlaylist();
	const deletePlaylistItems = useDeletePlaylistItems();
	const updatePlaylistItems = useUpdatePlaylistItems();

	const openEditPlaylistItemDialog = (_p: PlaylistItem) => {
		setPlaylistItem(_p);
		openMediaEditor();
	};

	const openDeletePlaylistItemDialog = (_p: PlaylistItem) => {
		setPlaylistItem(_p);
		openDialog();
	};

	const handleDeletePlaylistItems = (itemsToDelete) => {
		deletePlaylistItems.mutate({
			playlist,
			items: itemsToDelete,
		}, {
			onSuccess: closeDialog,
		});
	};

	const handleDropPlaylistItems = (source: PlaylistItem, target: PlaylistItem) => {
		console.log('dropped', source.id, 'on', target.id);

		const sourceIndex = playlist.items.findIndex(m => m.id === source.id);
		const targetIndex = playlist.items.findIndex(m => m.id === target.id);

		playlist.items.splice(targetIndex, 0, playlist.items.splice(sourceIndex, 1)[0]);

		queryClient.setQueryData(['playlists', id], playlist);
		
		updatePlaylistItems.mutate({
			playlist,
			items: playlist.items,
		});
	};

	const handlePaste = (input) => {
		console.log(input);

		let url: URL;
		let error: Error;

		try {
			url = new URL(input);
		} catch (urlParserError) {
			error = urlParserError;
		}
		
		// input was not an url, maybe an embedcode
		if (!url) {
			try {
				const parser = new DOMParser();
				const embedElement = parser.parseFromString(input, 'text/html');
				const iframe = embedElement.getElementsByTagName('iframe')[0];
				url = new URL(iframe?.getAttribute('src'));
					
			} catch (embedElementParserError) {
				error = embedElementParserError;
			}
		}
			
		if (!url) {
			// throw new Error(`Input "${input}" is not valid.`);
			// setPasteError(`Input "${input}" is not valid.`);

			toast({
				title: 'Your pasted url is not valid.',
				// description: `We're unable to parse "${input}".`,
				status: 'error',
				isClosable: true,
			});

			return;
		}
			
		updatePlaylist.mutate({
			id: playlist.id,
			items: [url],
		});

	};
	
	return (
		<LandingPage
			title={`Playlist: ${playlist.name} | Yoga!`}
			description={playlist.description}
		>			
			<Heading size="lg">Playlist: {playlist.name}</Heading>
			<Text>{playlist.description}</Text>
			<Text>{playlist.type.name}</Text>
			<Text>{playlist.level}</Text>

			<Text>Curated by:</Text>
			<UserCard user={playlist.owner}></UserCard>

			<Box>
				<Text>Categories</Text>
				<TagCloud
					tags={playlist.categories}
					hrefPrefix="/playlists/filter/categories"
					colorScheme="blue"
				/>
			</Box>
			<Box>
				<Text>Tags</Text>
				<TagCloud
					tags={playlist.tags}
					hrefPrefix="/playlists/filter/tags"
				/>
			</Box>
			
			{!!playlistItem && <AlertDialog
				isOpen={isOpenAlertDialog}
				submitDialog={() => handleDeletePlaylistItems([playlistItem])}
				openDialog={openDialog}
				closeDialog={closeDialog}
				title="Remove media from playlist"
				description={`Are you sure you want to remove media "${playlistItem.media.sourceTitle}" from this playlist?`}
			/>}
			<PlaylistEditor
				playlist={playlist}
				mediaTypes={mediaTypes}
				mediaLevels={MediaLevels}
				mediaCategories={categories}
				isOpen={isOpenEditor}
				closeEditor={closeEditor}
				openEditor={openEditor}
			/>
			<PlaylistControls
				playlist={playlist}
				onEditClick={openEditor}
				onAddMediaClick={openEditor}
				onDeleteClick={() => undefined}
			/>
			<MediaEditor
				media={playlistItem?.media}
				mediaTypes={mediaTypes}
				mediaLevels={MediaLevels}
				mediaDurations={[]}
				mediaCategories={categories}
				isOpen={isOpenMediaEditor}
				closeEditor={closeMediaEditor}
				openEditor={openMediaEditor}
			/>
			<PlaylistItems
				items={playlist.items}
				onItemDrop={handleDropPlaylistItems}
				controls={({ playlistItem }) => {
					return <PlaylistItemControls
						playlistItem={playlistItem}
						onEditClick={() => openEditPlaylistItemDialog(playlistItem)}
						onDeleteClick={() => openDeletePlaylistItemDialog(playlistItem)}
					/>;
				}}
			/>

			<PasteBox onPaste={handlePaste} />
			
			<Divider my={4} />

			<SimpleGrid
				as="footer"
				columns={3}
				gap={4}
				p={4}
				borderRadius={4}
				bgColor={'gray.100'}
			>
				<Box p={2}>
					<Heading size="md">
						Yoga by Status Code
					</Heading>
					<Text my={2}>
						Yoga is a community of people who share the same passion for learning and teaching.
					</Text>
					<Text my={2}>
						We're a community of people who share the same passion for learning and teaching.
					</Text>
					<Text my={2}>
						Copyright 2021 Status Code, version {version}.
					</Text>
				</Box>
				<Box
					backgroundColor="green.50"
					p={2}
					borderRadius={4}
				>
					<Heading size="md">Categories</Heading>
					<TagCloud
						tags={categories}
						hrefPrefix="/playlists/filter/categories"
						colorScheme="blue"
					/>
				</Box>
				<Box
					backgroundColor="pink.50"
					p={2}
					borderRadius={4}
				>
					<Heading size="md">Tags</Heading>
					<TagCloud
						tags={tags}
						hrefPrefix="/playlists/filter/tags"
					/>
				</Box>
			</SimpleGrid>

		</LandingPage>
	);
};

const PasteBox = ({ onPaste }) => {
	const handlePaste = useCallback(
		event => event.clipboardData.items[0].getAsString(input => onPaste(input)),
		[onPaste]
	);

	useEffect(() => {
		window.addEventListener('paste', handlePaste);

		return () => {
			window.removeEventListener('paste', handlePaste);
		};
	}, [handlePaste]);

	return (
		<Box
			my="2"
			p="2"
			borderRadius={4}
			bgColor="green.100"
		>
			<Heading size="md">Add media</Heading>
			<Text>Copy and paste Youtube urls here to add them to this playlist.</Text>
		</Box>
	);
};

const PlaylistDetailPageFallbackLoader: React.FC<PlaylistDetailPageProps> = (props: PlaylistDetailPageProps) => {
	const { isFallback } = useRouter();
	
	if (isFallback) {
		return <p>Loading page...</p>;
	}

	return <PlaylistDetailPage {...props} />;
};

export const getStaticPaths: GetStaticPaths = async () => {
	// todo: generate paths

	return {
		paths: [],
		fallback: true,
	};
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
	const id = parseInt(params.id as string);
	const playlist = await fetchPlaylist({ id });

	if (!playlist) {
		return {
			notFound: true,
		};
	}

	const mediaTypes = await fetchMediaTypes();
	const categories = await fetchCategories();
	const tags = await fetchTags();

	const queryClient = new QueryClient();
	queryClient.setQueryData(['playlists', id], playlist);
	queryClient.setQueryData(['mediaTypes'], mediaTypes);

	return {
		revalidate: 5,
		props: {
			id,
			categories,
			tags,
			dehydratedState: dehydrate(queryClient),
		},
	};
};

export default PlaylistDetailPageFallbackLoader;
